import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import HeroSearch from "../components/HeroSearch";
import RegistrarRow from "../components/RegistrarRow";
import RegistrarLogo from "../components/RegistrarLogo";
import Seo from "../components/Seo";
import { endpoints } from "../lib/api";
import { Loader2, Filter, AlertCircle, CheckCircle2, Info, Bookmark, Sparkles, Crown, ExternalLink, Gavel } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";

const SORTS = [
  { value: "cheapest", label: "Cheapest" },
  { value: "lowest_3y", label: "Lowest 3-yr" },
  { value: "best_bundle", label: "Best Bundle" },
  { value: "best_rated", label: "Best Rated" },
  { value: "lowest_renewal", label: "Lowest Renewal" },
  { value: "fastest_support", label: "Fastest Support" },
];

const SUPPORT_RANK = { fastest: 3, fast: 2, average: 1, slow: 0 };

export default function Compare() {
  const [params] = useSearchParams();
  const initial = params.get("name") || "";
  const [domain, setDomain] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState("cheapest");
  const { user, requestLogin } = useAuth();
  const { formatPrice } = useCurrency();

  const run = useCallback(async (name) => {
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      const res = await endpoints.domainCompare(name);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Lookup failed");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initial) {
      setDomain(initial);
      run(initial);
    }
  }, [initial, run]);

  const sortedRows = (() => {
    if (!data?.registrars) return [];
    const rows = [...data.registrars];
    rows.sort((a, b) => {
      switch (sort) {
        case "cheapest":
          return (a.register ?? 9999) - (b.register ?? 9999);
        case "lowest_3y":
          return (a.three_year_total ?? 99999) - (b.three_year_total ?? 99999);
        case "best_bundle":
          // Locked rates first, then highest bundle discount %
          if (a.three_year_bundle && !b.three_year_bundle) return -1;
          if (b.three_year_bundle && !a.three_year_bundle) return 1;
          return (b.bundle_discount_pct ?? 0) - (a.bundle_discount_pct ?? 0);
        case "best_rated":
          return b.trust_score - a.trust_score;
        case "lowest_renewal":
          return (a.renewal ?? 9999) - (b.renewal ?? 9999);
        case "fastest_support":
          return (SUPPORT_RANK[b.support_speed] ?? 0) - (SUPPORT_RANK[a.support_speed] ?? 0);
        default:
          return 0;
      }
    });
    return rows;
  })();

  const handleSave = async () => {
    if (!user) {
      toast("Sign in to save domains", { description: "We'll keep your watchlist synced across devices." });
      requestLogin("/dashboard");
      return;
    }
    try {
      await endpoints.savedCreate({ domain: data.domain, source: "search" });
      toast.success(`Saved ${data.domain}`);
    } catch (e) {
      toast.error("Could not save");
    }
  };

  return (
    <Layout>
      <Seo
        title={data?.domain ? `${data.domain} price comparison` : "Domain price comparison"}
        description={
          data?.domain
            ? `See live prices for ${data.domain} across various hosts. Registration, renewal and 3-year totals — verified by RDAP.`
            : "Compare cheap domain prices across various hosts. Live registration, renewal & transfer pricing — refreshed daily and verified by RDAP."
        }
        keywords="domain price comparison, cheap domains, cheapest domain registration, compare domain prices, cheap domain provider"
        path="/compare"
      />
      <section data-testid="compare-page">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Comparison engine</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">
          Every price. One glance.
        </h1>
        <p className="mt-3 max-w-2xl text-zinc-600">
          Live pricing and availability, refreshed every day. No stale rates, no hidden renewal traps, no clicks on listings that aren't actually sellable.
        </p>

        <div className="mt-6 max-w-2xl">
          <HeroSearch size="md" />
        </div>

        {loading && (
          <div className="mt-12 flex items-center gap-2 text-sm text-zinc-500" data-testid="compare-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking availability and pulling pricing…
          </div>
        )}

        {error && (
          <div className="mt-8 flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700" data-testid="compare-error">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}

        {data && !loading && (
          <div className="mt-10" data-testid="compare-results">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="tabular-nums text-2xl font-semibold text-zinc-900 break-all">{data.domain}</span>
                {data.available === true && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> Available
                  </span>
                )}
                {data.available === false && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                    <Info className="h-3 w-3" /> Already registered
                  </span>
                )}
                {data.available === null && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600">
                    <Info className="h-3 w-3" /> Could not verify
                  </span>
                )}
              </div>
              {data.available !== false && (
                <button onClick={handleSave} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-900 hover:border-indigo-200 hover:text-indigo-700" data-testid="save-domain-btn">
                  <Bookmark className="h-3.5 w-3.5" /> Save
                </button>
              )}
            </div>

            {/* Sort filters — full-width row below the header so they never clip */}
            {data.available !== false && (
              <div className="mt-5 flex items-center gap-2 overflow-x-auto pb-1" data-testid="sort-bar">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400 flex-shrink-0">
                  <Filter className="h-3 w-3" /> Sort
                </span>
                <div className="flex items-center gap-1 rounded-xl border border-zinc-200 bg-white p-1">
                  {SORTS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setSort(s.value)}
                      data-testid={`sort-${s.value}`}
                      className={`flex-shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                        sort === s.value ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Registry-premium disclaimer */}
            {data.available !== false && data.premium_possible && (
              <div
                data-testid="premium-warning"
                className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-5"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 text-amber-700" />
                  <div>
                    <div className="font-display text-sm font-semibold text-amber-900">Possible registry-premium domain</div>
                    <p className="mt-1 text-xs leading-relaxed text-amber-800/90">
                      GoDaddy is quoting this domain at a premium rate, which usually means the registry has flagged it as a high-tier name. Some registrars (esp. Porkbun, Cloudflare) refuse to sell registry-premium domains at standard prices — always confirm on the registrar's site before assuming the price below is what you'll pay.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Registrar comparison only when domain is available (or unverified) */}
            {data.available !== false && (
              <>
                <div className="mt-6 space-y-3">
                  {sortedRows.map((row) => (
                    <RegistrarRow key={row.slug} row={row} />
                  ))}
                </div>
                <p className="mt-6 text-xs text-zinc-400">
                  <span className="font-medium">Bundle</span> shows your savings vs paying the renewal price every year. <span className="font-medium">Locked rate</span> = no renewal hike. Click <span className="font-medium">Buy here</span> to open the same domain on the registrar's site.
                </p>
              </>
            )}

            {/* Domain is taken: show broker offers (if any) + alternatives */}
            {data.available === false && (
              <div className="mt-8 space-y-6" data-testid="taken-state">
                {data.broker_offers && data.broker_offers.length > 0 ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
                    <div className="flex items-center gap-2">
                      <Gavel className="h-4 w-4 text-amber-700" />
                      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-700">Broker / Aftermarket fee</span>
                    </div>
                    <p className="mt-2 text-sm text-amber-900">
                      This domain is registered, but a broker fee is being quoted for resale.
                    </p>
                    <div className="mt-4 space-y-2">
                      {data.broker_offers.map((b) => (
                        <div key={b.registrar} className="flex items-center justify-between rounded-xl border border-amber-200 bg-white p-4" data-testid={`broker-${b.registrar}`}>
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-100 bg-white">
                              <RegistrarLogo registrar={{ name: b.name, logo_domain: b.logo_domain, icon: "Server" }} size={24} />
                            </div>
                            <div>
                              <div className="font-display text-sm font-semibold text-zinc-900">{b.name}</div>
                              <div className="tabular-nums text-lg font-semibold text-amber-700">{formatPrice(b.price)}</div>
                            </div>
                          </div>
                          <a href={b.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800">
                            View offer <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-8">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Already registered</div>
                    <h3 className="mt-2 font-display text-xl font-semibold text-zinc-900">
                      {data.domain} is taken — and no public broker offer was found.
                    </h3>
                    <p className="mt-2 max-w-xl text-sm text-zinc-600">
                      We're not showing prices for taken domains. Try one of these instead:
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Link
                    to="/ai-finder"
                    data-testid="alt-ai"
                    className="group flex items-start gap-4 rounded-2xl border border-zinc-100 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md"
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display text-base font-semibold text-zinc-900">Generate brandable alternatives</div>
                      <p className="mt-1 text-sm text-zinc-600">Our AI will pitch brandable names like {data.domain} that are still available.</p>
                    </div>
                  </Link>
                  <Link
                    to="/premium-domains"
                    data-testid="alt-premium"
                    className="group flex items-start gap-4 rounded-2xl border border-zinc-100 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md"
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                      <Crown className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display text-base font-semibold text-zinc-900">Browse curated premium domains</div>
                      <p className="mt-1 text-sm text-zinc-600">Hand-picked names across AI, fintech, hotel tech, and more — ready to launch a brand.</p>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {!data && !loading && !error && (
          <div className="mt-16 rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/40 p-12 text-center" data-testid="compare-empty">
            <p className="font-display text-lg text-zinc-700">Enter a domain above to see prices.</p>
            <p className="mt-2 text-sm text-zinc-500">Example: <span className="font-mono">travelai.com</span></p>
          </div>
        )}
      </section>
    </Layout>
  );
}
